import { MemreButton, MemreFlexBox, MemreRTE } from 'components/core';
import { useAuthoringContext } from '../AuthoringContext';
import Sound from './Sound';
import { TConcept, TSound } from 'types';
import { debounce } from 'lodash';
import { useMutateConcept } from 'hooks/useMutateConcept';
import ImageButton from './ImageUpload/ImageButton';

interface AnswerFieldProps {
  concept: TConcept;
  label: string;
  // Update the answer in local state. This should not update the server.
  updateAnswerLocally: (answerId: string, updates: Partial<TConcept>) => void;
  // Called when the delete button is clicked. This should delete the answer from the server and local store.
  // If not provided, no delete button will be shown.
  didClickDelete?: () => void;
  backgroundColor?: string;
  allowSound: boolean;
}

export function AnswerField(props: AnswerFieldProps) {
  const { concept, label, updateAnswerLocally, didClickDelete, backgroundColor, allowSound } = props;
  const { set: assignment, item } = useAuthoringContext();
  const [mutateConcept] = useMutateConcept(item?.id, assignment?.id);

  const debouncedAnswerTextChange = debounce(async (content: string) => {
    const payload = {
      data: {
        attributes: {
          'text-html': content,
        },
      },
    };

    await mutateConcept({ id: concept.id, payload });
    updateAnswerLocally(concept.id, { text: content });
  }, 500);

  const updateImage = async (imageData: { id: string; url: string } | null) => {
    const payload = {
      data: {
        relationships: {
          image: {
            data: imageData ? { id: imageData.id } : null,
          },
        },
      },
    };

    await mutateConcept({ id: concept.id, payload });
    updateAnswerLocally(concept.id, { image: imageData });
  };

  const updateSoundData = async (soundData: TSound | null) => {
    const payload = {
      data: {
        relationships: {
          sound: {
            data: soundData ? { id: soundData.id } : null,
          },
        },
      },
    };

    await mutateConcept({ id: concept.id, payload });
    updateAnswerLocally(concept.id, { sound: soundData });
  };

  const handleAddImage = async (imageUrl: string, imageId: string) => {
    await updateImage({ id: imageId, url: imageUrl });
  };

  const handleDeleteImage = async () => {
    await updateImage(null);
  };

  const handleAddSound = async (soundUrl: string, soundId: string) => {
    await updateSoundData({ id: soundId, url: soundUrl });
  };

  const handleDeleteSound = async () => {
    await updateSoundData(null);
  };

  const imageUrl = concept.image?.url;

  return (
    <>
      <div>
        <MemreFlexBox direction="row" gap={2} sx={{ width: '100%' }}>
          <div
            style={{
              flex: 1,
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px',
              backgroundColor: 'inherit',
              position: 'relative',
              paddingTop: '1px',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '-6px',
                left: '10px',
                padding: '0 5px',
                fontSize: '12px',
                backgroundColor: backgroundColor ?? 'white',
                color: 'rgba(0, 0, 0, 0.6)',
              }}
            >
              {label}
            </div>
            <MemreRTE
              initialContent={concept.text ?? ''}
              onUpdate={debouncedAnswerTextChange}
              placeholder={`Type your answer here...`}
              id={`$answer-${concept.id}`}
            />
          </div>

          <ImageButton
            imageUrl={imageUrl}
            onAddImage={handleAddImage}
            onDeleteImage={handleDeleteImage}
            buttonSize="small"
            imageHeight={56}
          />

          {allowSound && <Sound url={concept.sound?.url} add={handleAddSound} handleDelete={handleDeleteSound} />}
          {didClickDelete && (
            <MemreButton 
              variant="icon" 
              iconName="trash" 
              onClick={didClickDelete} 
              sx={{ 
                backgroundColor: 'white', 
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                minWidth: '32px',
                alignSelf: 'center',
                display: 'flex',
                justifyContent: 'center'
              }} 
            />
          )}
        </MemreFlexBox>
      </div>
    </>
  );
}
